import React from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  SelectInput,
  FormDataConsumer,
  EditProps,
  Toolbar,
  SaveButton,
  required,
  useEditContext,
  BooleanInput,
  NumberInput,
} from "react-admin";
import { Typography, Box } from "@mui/material";
import firebaseDataProvider from "../../providers/dataProvider";
import CategoryEditActions from "./CategoryEditActions";
import FirebaseImageInput from "../../components/FirebaseImageInput";
import { LayoutType, categoryVersionForLayout } from "../../types";

// Custom toolbar that shows only the Save button.
const CategoryEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

// Custom form that overrides the record's images field.
// We remove any existing "images" value and compute it from "entries".
const CustomEditForm: React.FC<any> = (props) => {
  const { record } = useEditContext();
  if (!record) return <div>Loading...</div>;

  // Compute images from entries for banner type.
  // The Edit form reads from the categories/ collection, which after a sync pass holds
  // [small, middle, big] webp variants in `assets` and the source image in `assetOriginal`.
  // Show the source for editing so admins see (and re-save) the full-quality original,
  // not the small variant.
  const computedImages = record.entries
    ? record.entries.map((entry: any) => ({
        image: entry.assetOriginal ?? entry.assets?.[0],
        referenceId: entry.referenceId,
        id: entry.id,
      }))
    : [];

  // Create a new record overriding any existing images.
  const computedRecord = {
    ...record,
    images: computedImages,
  };

  return (
    <SimpleForm
      {...props}
      record={computedRecord}
      initialvalues={computedRecord}
    />
  );
};

// Map a form `images` row back into a banner entry. The override doc only ever holds
// the source image in `assets[0]` — variants and assetOriginal are sync-service-managed
// artifacts that live in the categories/ collection and are regenerated every sync run.
const mapImageRowToEntry = (item: any) => ({
  id: item.id || `ca${Math.random().toString(36).substring(2, 15)}`,
  assets: [item.image],
  referenceId: item.referenceId || "",
  type: "banner",
  title: "",
  description: "",
  displayName: "",
  artists: [],
  subItemCount: 1,
});

const CategoryEdit = (props: Omit<EditProps, "children">) => {
  // onSuccess callback: convert "images/thumbnail" back to "entries" for the backend.
  const onSuccess = async (response: any) => {
    let updatedData = { ...response };

    if (response.layoutType === LayoutType.BANNER && updatedData.images) {
      updatedData.entries = updatedData.images.map(mapImageRowToEntry);
    }

    // Clean up form-only fields
    delete updatedData.images;

    try {
      // Admin-created categories (isAdminCreated=true) are saved to sync/override/extra
      // Categories from sync (Airtable/schedule) should have overrides saved to sync/override/categories
      if (response.isAdminCreated) {
        // Admin-created category - save to extra (will be synced to main categories)
        await firebaseDataProvider.set("sync/override/extra", {
          id: updatedData.id,
          data: updatedData,
          previousData: response,
        });
      } else {
        // Existing category from sync - save override to categories
        await firebaseDataProvider.set("sync/override/categories", {
          id: updatedData.id,
          data: updatedData,
          previousData: response,
        });
      }
      console.log("Override updated successfully");

      // Remove a hidden category from the public collection right away instead of waiting
      // for the next sync. Done only after the override is saved, so the sync keeps it hidden.
      if (updatedData.isHidden) {
        await firebaseDataProvider.delete("categories", { id: updatedData.id });
        console.log("Hidden category removed from categories");
      }
    } catch (error: any) {
      console.error("Error updating override document", error);
    }
  };

  const transform = (data: any) => {
    let entries: any[] = [];

    if (data.layoutType === LayoutType.BANNER && data.images) {
      entries = data.images.map(mapImageRowToEntry);
    } else {
      // Keep existing entries for other layout types
      entries = data.entries || [];
    }

    return {
      ...data,
      entries,
      // Clients query categories with a hardcoded `version` filter, so this has to track
      // layoutType even on edit — otherwise switching a category to a version-gated layout
      // (or back) would leave the old version and either stay invisible or ship too early.
      version: categoryVersionForLayout(data.layoutType),
    };
  };

  return (
    <Edit
      transform={transform}
      actions={<CategoryEditActions />}
      resource="categories"
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      {...props}
    >
      <CustomEditForm toolbar={<CategoryEditToolbar />}>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            ⚠️ Please note: After editing a category, it may take up to 24 hours
            for the changes to appear in the app or on the website due to
            caching.
          </Typography>
        </Box>
        {/* Display the category name - editable for admin-created categories */}
        <FormDataConsumer>
          {({ formData }) => (
            <TextInput
              source="name"
              disabled={!formData?.isAdminCreated}
            />
          )}
        </FormDataConsumer>

        {/* Category Type selector */}
        <SelectInput
          source="layoutType"
          label="Category Type"
          choices={[
            { id: LayoutType.BANNER, name: "Banner" },
            { id: LayoutType.VIDEO, name: "Video" },
            { id: LayoutType.SIMPLE_LIST, name: "Simple List" },
            { id: LayoutType.GALLERY_SPLIT, name: "Gallery Split" },
            { id: LayoutType.GENRE, name: "Genre" },
            { id: LayoutType.GRID, name: "Grid" },
          ]}
          validate={required()}
        />

        <NumberInput source="priority" label="Priority" helperText="Lower number = higher priority (shown first)" />

        <BooleanInput
          source="isPrimary"
          label="Is Primary"
          helperText="When enabled, category will be shown on the home tab"
        />

        <BooleanInput
          source="isHidden"
          label="Hidden"
          helperText="When enabled, the category is removed from the app and website. It stays listed here so it can be unhidden."
        />

        {/* Conditionally render the Banner Images ArrayInput only if layoutType is "banner" */}
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData && formData.layoutType === LayoutType.BANNER ? (
              <ArrayInput source="images" label="Banner Images" {...rest}>
                <SimpleFormIterator>
                  <FirebaseImageInput
                    source="image"
                    label="Banner"
                    validate={required()}
                  />
                  <TextInput
                    source="referenceId"
                    label="Reference URL"
                    placeholder="Enter a specific URL or referenceId"
                  />
                </SimpleFormIterator>
              </ArrayInput>
            ) : null
          }
        </FormDataConsumer>

        {/* Toggle to make episodes always visible for shows in this category */}
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData && formData.layoutType !== LayoutType.BANNER ? (
              <BooleanInput
                {...rest}
                source="episodesAlwaysVisible"
                label="Episodes Always Visible"
                helperText="When enabled, all episodes in shows belonging to this category will always be visible (no air date or expiration required)"
              />
            ) : null
          }
        </FormDataConsumer>

        {/* Thumbnail input: shown only for admin-created categories */}
        <FormDataConsumer>
          {({ formData }) =>
            formData && formData.isAdminCreated ? (
              <FirebaseImageInput
                source="thumbnail"
                label="Category Thumbnail"
              />
            ) : null
          }
        </FormDataConsumer>
      </CustomEditForm>
    </Edit>
  );
};

export default CategoryEdit;
